import React from 'react'

import { Stack, Typography } from '@mui/material'

import { Modal } from '@repo/ui'

import { Slideshow } from 'components/display/Slideshow'

interface ImageGalleryModalProps {
  open: boolean
  onClose: () => void
  /** Image URLs in the order they are listed on the surface that opened this. */
  images: string[]
  /** Which one to open on — the thumbnail that was clicked. */
  initialIndex?: number
  /**
   * Modal heading. Defaults to the neutral "Images" rather than a filename (which can be long
   * enough to wrap the header) or "Attachments" (which is only true on the support form — it
   * read as the wrong word over a product's media).
   */
  title?: string
}

/**
 * Full-size image viewer: a modal wrapping the shared `Slideshow`, opened on a chosen image.
 *
 * ## Why this wraps `Slideshow` rather than being its own carousel
 *
 * `Slideshow` already solves the hard parts — swiper wiring, prev/next, the thumbnail strip
 * with an active-state border, and keeping the strip in sync with the main slide. A second
 * carousel built beside it is the exact "second hand-roll" working-conventions §36 says to
 * stop at: two components rendering the same interaction, drifting apart the moment either is
 * touched. So the two things a lightbox needs that a product card doesn't — opening on a given
 * index, and filling the available height instead of a square — became `initialIndex` and
 * `fill` props ON `Slideshow`, and every future surface gets them too.
 *
 * ## Ordering
 *
 * `images` is passed in the SAME order the calling surface lists them, so the slideshow's
 * order matches what the user is looking at. Callers holding a mixed list (images plus PDFs)
 * must filter to the renderable ones and map the clicked row's index into the filtered array —
 * an `<img>` cannot display a PDF, so passing one through would open a blank slide.
 */
export const ImageGalleryModal = ({
  open,
  onClose,
  images,
  initialIndex = 0,
  title = 'Images',
}: ImageGalleryModalProps) => {
  // The modal is deliberately much wider than the 533px default — the entire purpose is size.
  // Capped against the viewport so it never overflows on a laptop screen.
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      titleAdornment={
        images.length > 1 ? (
          <Typography variant="body3" color="text.secondary">
            {images.length} images
          </Typography>
        ) : undefined
      }
      sx={{ width: 'min(920px, calc(100vw - 64px))' }}
      bodySx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      <Stack sx={{ height: 'min(70vh, 640px)', minHeight: 0 }}>
        <Slideshow images={images} initialIndex={initialIndex} fill />
      </Stack>
    </Modal>
  )
}
